import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { EmbedBadge } from "./EmbedBadge";
import { apiClient } from "@/lib/api-client";
import type { Station } from "@/lib/api-client";

type EmbedTheme = "dark" | "light";

interface ScriptEmbedPreviewProps {
	station: string;
	theme: EmbedTheme;
}

// Create a singleton QueryClient for the embed preview
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: 1,
		},
	},
});

/**
 * Inner component that fetches station data and renders EmbedBadge
 */
function EmbedBadgeWrapper({ station, theme }: ScriptEmbedPreviewProps) {
	// Parse station prop (format: "username/slug")
	const [username, slug] = station.split("/");

	// Fetch station data from API
	const { data: stationData, isLoading, error } = useQuery({
		queryKey: ["station", username, slug],
		queryFn: async () => {
			const data = await apiClient.users.getStationByUsernameAndSlug(username, slug);
			return data;
		},
		enabled: !!username && !!slug,
	});

	// Loading state
	if (isLoading) {
		return (
			<div className="w-full flex items-center justify-center p-4">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto mb-2"></div>
					<p className="text-sm text-gray-500">Loading station...</p>
				</div>
			</div>
		);
	}

	// Error state
	if (error || !stationData) {
		return (
			<div className="w-full flex items-center justify-center p-4">
				<div className="text-center">
					<p className="text-sm text-red-500 mb-1">Station not found</p>
					<p className="text-xs text-gray-400">{station}</p>
				</div>
			</div>
		);
	}

	// Render the actual EmbedBadge component
	return <EmbedBadge station={stationData} theme={theme} />;
}

/**
 * Component to preview the script tag embed by directly rendering
 * the EmbedBadge React component (same component used in the web component)
 */
export function ScriptEmbedPreview({
	station,
	theme,
}: ScriptEmbedPreviewProps) {
	return (
		<QueryClientProvider client={queryClient}>
			<EmbedBadgeWrapper station={station} theme={theme} />
		</QueryClientProvider>
	);
}
